<template>
  <v-sheet
    dark
    id="video-player-controller"
    v-show="shows || $data.$_isSeekingBySlider"
    v-bind="$attrs"
    v-on:mousedown.stop
  >
    <video-player-seek-bar
      v-bind:current-time="playTime"
      v-bind:video-time-range="videoTimeRange"
      v-bind:duration="duration"
      v-bind:is-loop-enabled="isLoopEnabled"
      v-bind:loop-definition="loopDefinition"
      v-bind:sorted-marker-segments="sortedMarkerSegments"
      v-on:seek="$_seekBySlider"
      v-on:seek-start="$_seekBySliderStart"
      v-on:seek-end="$_seekBySliderEnd"
    >
    </video-player-seek-bar>

    <div class="d-flex align-center">
      <v-btn
        icon data-testid="cue"
        v-on:mousedown="$_seekToPreviousMarker"
      >
        <v-icon>mdi-skip-previous</v-icon>
      </v-btn>

      <v-btn
        icon data-testid="rewind"
        v-on:mousedown="$_seekBackwardStart"
        v-on:mouseup="$_seekBackwardEnd"
        v-on:mouseout="$_seekBackwardEnd"
      >
        <v-icon>mdi-rewind</v-icon>
      </v-btn>

      <v-btn
        v-if="isPlaying"
        icon data-testid="pause"
        v-on:mousedown="$_pause"
      >
        <v-icon >mdi-pause</v-icon>
      </v-btn>

      <v-btn
        v-else
        icon data-testid="play"
        v-on:mousedown="$_play"
      >
        <v-icon>mdi-play</v-icon>
      </v-btn>

      <v-btn
        icon data-testid="fast-forward"
        v-on:mousedown="$_seekForwardStart"
        v-on:mouseup="$_seekForwardEnd"
        v-on:mouseout="$_seekForwardEnd"
      >
        <v-icon>mdi-fast-forward</v-icon>
      </v-btn>

      <v-btn
        icon data-testid="go-to-end"
        v-on:mousedown="$_seekToNextMarker"
      >
        <v-icon>mdi-skip-next</v-icon>
      </v-btn>

      <v-spacer></v-spacer>

      <div class="d-flex flex-shrink-1 align-center overflow-hidden">
        <video-player-time-indicator v-bind:time="playTime"></video-player-time-indicator>

        <text-devider content="/" v-bind:margin-px="3"></text-devider>

        <video-player-time-indicator v-bind:time="duration"></video-player-time-indicator>
      </div>

      <v-spacer></v-spacer>

      <v-btn
        icon data-testid="loop"
        v-on:mousedown="$_toggleEnableLoopFlag"
      >
        <v-icon v-bind:class="$_loopIconClass">mdi-restore</v-icon>
      </v-btn>

      <v-menu
        top left offset-y
        v-bind:open-on-hover="!$data.$_isSeekingBySlider"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            data-testid="volumeRate"
            id="volume-rate-indicator"
            v-bind="attrs"
            v-on="on"
            v-on:mousedown.exact="$_toggleVideoMuted"
          >
            <v-icon>{{ $_volumeIconName }}</v-icon>
          </v-btn>
        </template>

        <v-card>
          <v-card-text dense class="pa-2">
            <v-slider
              dense vertical min="0" max="100" step="1"
              prepend-icon="mdi-volume-low"
              append-icon="mdi-volume-high"
              id="volume-rate-slider"
              v-model="$_videoVolumePercentage"
            >
            </v-slider>
          </v-card-text>
        </v-card>
      </v-menu>

      <v-menu
        top left offset-y
        v-bind:open-on-hover="!$data.$_isSeekingBySlider"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            data-testid="playback-rate"
            id="playback-rate-indicator"
            v-bind="attrs"
            v-on="on"
            v-on:mousedown.exact="$_forwardPlaybackRate"
            v-on:mousedown.shift.exact="$_backwardPlaybackRate"
          >
            {{ $_playbackRateString }}x
          </v-btn>
        </template>

        <v-list dense id="playback-rate-list" class="pa-2">
          <v-list-item
            class="playback-rate-item pa-1"
            v-for="(playbackRateString, playbackRateIdx) of $_allPlaybackRateStrings"
            v-bind:key="playbackRateString"
            v-on:mousedown="$_setPlaybackRate(playbackRateIdx)"
          >
            <v-list-item-title>{{ playbackRateString }}x</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </v-sheet>
</template>

<style scoped>
#video-player-controller {
  user-select: none;
}

#volume-rate-indicator,
#playback-rate-indicator {
  font-size: 11px;
}

#volume-rate-slider {
  min-width: 30px;
}
#playback-rate-list .v-list-item {
  user-select: none;
  text-align: center;
  min-height: 20px;
}
</style>

<style src="./video_player.css" scoped></style>

<script>
import VideoPlayerSeekBar from './VideoPlayerSeekBar.vue';
import VideoPlayerTimeIndicator from './VideoPlayerTimeIndicator.vue';
import TextDevider from '../Parts/TextDivider.vue';
import LoopDefinition from '../../modules/LoopDefinition.js';
import TimeValue from '../../modules/TimeValue.js';
import VideoTimeRange from './modules/VideoTimeRange.js';

const seekIntervalTime = TimeValue.msec(100);
const seekTimeStep = TimeValue.sec(1);

export default {
  components: {
    VideoPlayerSeekBar,
    VideoPlayerTimeIndicator,
    TextDevider,
  },

  watch: {
    videoPlaybackRate: {
      handler(videoPlaybackRate) {
        for (let playbackRateIdx = 0; playbackRateIdx < this.$_numPlaybackRates; playbackRateIdx++) {
          if (videoPlaybackRate <= Number(this.$_allPlaybackRateStrings[playbackRateIdx])) {
            this.$_setPlaybackRate(playbackRateIdx);
            break;
          }
        }
      },
      immediate: true,
    }
  },

  props: {
    shows:                { type: Boolean,        default: false },
    duration:             { type: TimeValue,      default: () => TimeValue.zero },
    playTime:             { type: TimeValue,      default: () => TimeValue.zero },
    videoTimeRange:       { type: VideoTimeRange },
    videoPlaybackRate:    { type: Number,         default: 1 },
    videoVolumeRate:      { type: Number,         default: 1 },
    videoMuted:           { type: Boolean,        default: false },
    isPlaying:            { type: Boolean,        default: false },
    isLoopEnabled:        { type: Boolean,        default: false },
    loopDefinition:       { type: LoopDefinition, default: undefined },
    sortedMarkerSegments: { type: Array,          default: () => ([]) },
  },

  data() {
    return {
      $_isPlayingOnSeek: undefined,
      $_isSeeking: false,
      $_isSeekingBySlider: false,
      $_seekTargetTime: TimeValue.zero,
      $_intervalId: undefined,
      $_playbackRateIdx: 4,
      $_seekToPreviousMarkerTimeoutId: undefined,
    };
  },

  computed: {
    $_loopIconClass() {
      return (this.isLoopEnabled)? [ 'loop' ] : undefined;
    },

    $_allPlaybackRateStrings() {
      return [ '0.1', '0.25', '0.5', '0.75', '1', '1.5', '2', '3', '4' ]
    },

    $_numPlaybackRates() {
      return this.$_allPlaybackRateStrings.length;
    },

    $_playbackRateString() {
      return this.$_allPlaybackRateStrings[this.$data.$_playbackRateIdx];
    },

    $_playbackRate() {
      return Number(this.$_playbackRateString);
    },

    $_sortedMarkerSegmentBeginTimes() {
      let sortedMarkerSegmentBeginTimes = []
      for (let markerSegment of this.sortedMarkerSegments) {
        let markerSegmentBeginTime = markerSegment.begin;
        if (markerSegmentBeginTime === null) markerSegmentBeginTime = TimeValue.zero;
        if (sortedMarkerSegmentBeginTimes.length > 0) {
          let lastMarkerSegmentBeginTime = sortedMarkerSegmentBeginTimes[sortedMarkerSegmentBeginTimes.length - 1];
          if (markerSegmentBeginTime.isEqualTo(lastMarkerSegmentBeginTime)) continue;
        }
        sortedMarkerSegmentBeginTimes.push(markerSegmentBeginTime)
      }
      return sortedMarkerSegmentBeginTimes;
    },

    $_previousMarkerSegmentBeginTime() {
      let isSkippedOnce = false;
      for (let markerKindIdx = (this.$_sortedMarkerSegmentBeginTimes.length - 1); markerKindIdx >= 0; --markerKindIdx) {
        let markerKindBeginTime = this.$_sortedMarkerSegmentBeginTimes[markerKindIdx];
        if (markerKindBeginTime === null) markerKindBeginTime = TimeValue.zero;
        if (markerKindBeginTime.isGreaterThanOrEqualTo(this.playTime)) continue;
        if (!isSkippedOnce && (this.$data.$_seekToPreviousMarkerTimeoutId !== undefined)) {
          isSkippedOnce = true;
          continue;
        }
        return markerKindBeginTime;
      }
      return TimeValue.zero;
    },

    $_nextMarkerSegmentBeginTime() {
      for (let markerKindBeginTime of this.$_sortedMarkerSegmentBeginTimes) {
        if (markerKindBeginTime === null) continue;
        if (markerKindBeginTime.isLessThanOrEqualTo(this.playTime)) continue;
        return markerKindBeginTime;
      }
      return this.duration;
    },

    $_volumeIconName() {
      if (this.videoMuted) return 'mdi-volume-off';
      if (this.videoVolumeRate > 0.6) return 'mdi-volume-high';
      if (this.videoVolumeRate > 0.3) return 'mdi-volume-medium';
      if (this.videoVolumeRate > 0) return 'mdi-volume-low';
      return 'mdi-volume-off';
    },

    $_videoMuted: {
      get()           { return this.videoMuted },
      set(videoMuted) { this.$emit('update:video-muted', videoMuted) },
    },

    $_videoVolumePercentage: {
      get()                {
        if (this.videoMuted) return 0;
        return this.videoVolumeRate * 100;
      },
      set(videoVolumeRate) {
        if (videoVolumeRate > 0) this.$_videoMuted = false;
        this.$emit('update:video-volume-rate', videoVolumeRate / 100);
      },
    },
  },

  methods: {
    $_seekToPreviousMarker() {
      this.$emit('seek', this.$_previousMarkerSegmentBeginTime);
      if (this.$data.$_seekToPreviousMarkerTimeoutId !== undefined) {
        window.clearTimeout(this.$data.$_seekToPreviousMarkerTimeoutId);
        this.$data.$_seekToPreviousMarkerTimeoutId = undefined;
      }
      if (this.isPlaying) {
        this.$data.$_seekToPreviousMarkerTimeoutId = window.setTimeout(
          () => { this.$data.$_seekToPreviousMarkerTimeoutId = undefined },
          200,
        );
      }
    },

    $_seekToNextMarker() {
      this.$emit('seek', this.$_nextMarkerSegmentBeginTime);
    },

    $_toggleEnableLoopFlag() {
      this.$emit('update:is-loop-enabled', !this.isLoopEnabled);
    },

    $_play() {
      this.$emit('play');
    },

    $_pause() {
      this.$emit('pause');
    },

    $_seekBackwardStart() {
      if (this.$data.$_isSeeking) return;
      this.$data.$_isSeeking = true;
      this.$data.$_isPlayingOnSeek = this.isPlaying;
      if (this.$data.$_isPlayingOnSeek) this.$_pause();
      this.$data.$_seekTargetTime.assign(this.playTime);
      this.$_updateSeekBackwardTargetTime();
      this.$_setInterval(this.$_updateSeekBackwardTargetTime, seekIntervalTime);
    },

    $_updateSeekBackwardTargetTime() {
      let nextSeekTargetTime = this.$data.$_seekTargetTime.subtract(seekTimeStep).floorTo(TimeValue.zero);
      this.$data.$_seekTargetTime.assign(nextSeekTargetTime);
      this.$emit('seek', nextSeekTargetTime);
    },

    $_seekBackwardEnd() {
      if (!this.$data.$_isSeeking) return;
      this.$_clearInterval();
      if (this.$data.$_isPlayingOnSeek) this.$_play();
      this.$data.$_isPlayingOnSeek = undefined;
      this.$data.$_isSeeking = false;
    },

    $_seekForwardStart() {
      if (this.$data.$_isSeeking) return;
      this.$data.$_isSeeking = true;
      this.$data.$_isPlayingOnSeek = this.isPlaying;
      if (this.$data.$_isPlayingOnSeek) this.$_pause();
      this.$data.$_seekTargetTime.assign(this.playTime);
      this.$_updateSeekForwardTargetTime();
      this.$_clearInterval();
      this.$_setInterval(this.$_updateSeekForwardTargetTime, seekIntervalTime);
    },

    $_updateSeekForwardTargetTime() {
      let nextSeekTargetTime = this.$data.$_seekTargetTime.add(seekTimeStep).ceilTo(this.duration);
      this.$data.$_seekTargetTime.assign(nextSeekTargetTime);
      this.$emit('seek', nextSeekTargetTime);
    },

    $_seekForwardEnd() {
      if (!this.$data.$_isSeeking) return;
      this.$_clearInterval();
      if (this.$data.$_isPlayingOnSeek && this.playTime.isLessThan(this.videoTimeRange.end)) {
        this.$_play();
      }
      this.$data.$_isPlayingOnSeek = undefined;
      this.$data.$_isSeeking = false;
    },

    $_seekBySliderStart() {
      this.$data.$_isSeekingBySlider = true;
      this.$data.$_isPlayingOnSeek = this.isPlaying;
      if (this.$data.$_isPlayingOnSeek) this.$_pause();
    },

    $_seekBySliderEnd() {
      if (this.$data.$_isPlayingOnSeek) this.$_play();
      this.$data.$_isPlayingOnSeek = undefined;
      this.$data.$_isSeekingBySlider = false;
    },

    $_seekBySlider(time) {
      if (!this.$data.$_isSeekingBySlider) return;
      this.$emit('seek', time);
    },

    $_clearInterval() {
      if (this.$data.$_intervalId !== undefined) window.clearInterval(this.$data.$_intervalId);
      this.$data.$_intervalId = undefined;
    },

    $_setInterval(callback, interval) {
      this.$_clearInterval();
      this.$data.$_intervalId = window.setInterval(callback, interval.msec);
    },

    $_setPlaybackRate(playbackRateIdx) {
      this.$data.$_playbackRateIdx = playbackRateIdx;
      this.$emit('update:video-playback-rate', this.$_playbackRate);
    },

    $_toggleVideoMuted() {
      this.$_videoMuted = !this.$_videoMuted;
    },

    $_forwardPlaybackRate() {
      let nextPlaybackRateIdx = this.$data.$_playbackRateIdx + 1;
      if (this.$_allPlaybackRateStrings.length <= nextPlaybackRateIdx) nextPlaybackRateIdx = 0;
      this.$_setPlaybackRate(nextPlaybackRateIdx);
    },

    $_backwardPlaybackRate() {
      let nextPlaybackRateIdx = this.$data.$_playbackRateIdx - 1;
      if (0 > nextPlaybackRateIdx) nextPlaybackRateIdx = (this.$_allPlaybackRateStrings.length - 1);
      this.$_setPlaybackRate(nextPlaybackRateIdx);
    },
  },
}
</script>